import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronUp, Plus, Eye, Trash2, Loader2, X } from 'lucide-react';
import _ from 'lodash';

import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Card, CardContent } from "../components/ui/Card";
import { Spinner } from "../components/ui/Spinner";
import { ConfirmationDialog } from '../components/ConfirmationDialog';
import { useToast } from "../components/ui/Toast";

import { Playbook } from '../types';
import { usePlaybooksApi } from '../apis/playbooks-apis';

const ITEMS_PER_PAGE = 20;

export const PlaybooksList: React.FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const playbooksApi = usePlaybooksApi();
  const containerRef = useRef<HTMLDivElement>(null);
  const isMounted = useRef(false);

  const [playbooks, setPlaybooks] = useState<Playbook[]>([]);
  const [displayedPlaybooks, setDisplayedPlaybooks] = useState<Playbook[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Playbook[] | null>(null);

  const [showScrollTop, setShowScrollTop] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [playbookToDelete, setPlaybookToDelete] = useState<string | null>(null);
  const [isDeletingId, setIsDeletingId] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    const fetchInitialData = async () => {
      try {
        setLoading(true);
        const getAllPlaybooks = false;
        const fetchedPlaybooks = await playbooksApi.getPlaybooks(getAllPlaybooks);

        if (isMounted.current) {
          setPlaybooks(fetchedPlaybooks);
          setTotalCount(fetchedPlaybooks.length);
          setDisplayedPlaybooks(fetchedPlaybooks.slice(0, ITEMS_PER_PAGE));
          setHasMore(fetchedPlaybooks.length > ITEMS_PER_PAGE);
        }
      } catch (error: unknown) {
        if (isMounted.current) {
          if (error instanceof Error && error.name !== 'AbortError') {
            console.error('Error fetching playbooks:', error);
            addToast({
              title: "Error",
              description: "Failed to retrieve playbooks. Please try again.",
              variant: "destructive",
            });
          }
        }
      } finally {
        if (isMounted.current) {
          setLoading(false);
        }
      }
    };

    isMounted.current = true;
    fetchInitialData();

    return () => {
      isMounted.current = false;
      controller.abort();
    };
  }, []);

  const loadMorePlaybooks = useCallback(() => {
    if (searchTerm || loadingMore || !hasMore) return;

    setLoadingMore(true);
    const startIndex = displayedPlaybooks.length;
    const endIndex = startIndex + ITEMS_PER_PAGE;

    if (startIndex >= playbooks.length) {
      setHasMore(false);
      setLoadingMore(false);
      return;
    }

    const newItems = playbooks.slice(startIndex, endIndex);
    setDisplayedPlaybooks(prev => [...prev, ...newItems]);

    if (endIndex >= playbooks.length) {
      setHasMore(false);
    }

    setLoadingMore(false);
  }, [searchTerm, loadingMore, hasMore, playbooks, displayedPlaybooks.length]);

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '100px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasMore && !loadingMore && !searchTerm) {
        loadMorePlaybooks();
      }
    }, options);

    const loader = document.querySelector('#scroll-loader');
    if (loader) {
      observer.observe(loader);
    }

    return () => observer.disconnect();
  }, [loadMorePlaybooks, hasMore, loadingMore, searchTerm]);

  const handleSearch = _.debounce((term: string) => {
    setSearchTerm(term);
    if (term) {
      const results = playbooks.filter(playbook =>
        playbook.playbookName.toLowerCase().includes(term.toLowerCase()) ||
        (playbook.playbookProductName && playbook.playbookProductName.toLowerCase().includes(term.toLowerCase())) ||
        (playbook.playbookDescription?.toLowerCase().includes(term.toLowerCase())) ||
        (playbook.playbookTags?.some(tag => tag.toLowerCase().includes(term.toLowerCase())))
      );
      setSearchResults(results);
    } else {
      setSearchResults(null);
      setDisplayedPlaybooks(playbooks.slice(0, ITEMS_PER_PAGE));
      setHasMore(playbooks.length > ITEMS_PER_PAGE);
    }
  }, 300);

  const handleViewPlaybook = (playbookId: string) => {
    navigate(`/studio/playbooks/edit/${playbookId}`);
  };

  const handleDeleteClick = (id: string) => {
    setPlaybookToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirmed = async () => {
    if (!playbookToDelete) return;

    setIsDeletingId(playbookToDelete);
    try {
      await playbooksApi.deletePlaybook(playbookToDelete);
      addToast({
        title: "Success",
        description: "Playbook successfully deleted",
        variant: "default",
      });

      try {
        const getAllPlaybooks = false;
        const fetchedPlaybooks = await playbooksApi.getPlaybooks(getAllPlaybooks);
        setPlaybooks(fetchedPlaybooks);
        setTotalCount(fetchedPlaybooks.length);
        setDisplayedPlaybooks(fetchedPlaybooks.slice(0, ITEMS_PER_PAGE));
        setHasMore(fetchedPlaybooks.length > ITEMS_PER_PAGE);
      } catch (error) {
        console.error('Error refreshing playbooks:', error);
      }
    } catch (error) {
      addToast({
        title: "Error",
        description: "Failed to delete playbook. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeletingId(null);
      setDeleteDialogOpen(false);
      setPlaybookToDelete(null);
    }
  };

  useEffect(() => {
    const handleScroll = _.throttle(() => {
      setShowScrollTop(window.scrollY > 400);
    }, 200);

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const currentPlaybooks = searchResults || displayedPlaybooks;
  const currentTotal = searchResults ? searchResults.length : totalCount;

  return (
    <div className="container mx-auto p-8" ref={containerRef}>
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
        Playbooks Management
      </h1>
      <p className="text-sm font-medium text-gray-500 text-muted-foreground mt-1 mb-6">
        Configure and manage playbooks for your agents.
      </p>

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div className="w-full sm:max-w-md">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <Input
              type="text"
              placeholder="Search playbooks by name, description, or tags..."
              className="w-full pl-10 pr-8 py-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                handleSearch(e.target.value);
              }}
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm('');
                  handleSearch('');
                }}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
        <Button
          variant="default"
          className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white transition-colors duration-200"
          onClick={() => navigate('/studio/playbooks/add')}
        >
          <Plus className="mr-2 h-4 w-4" />
          Add New Playbook
        </Button>
      </div>

      <div className="text-sm text-muted-foreground mb-4">
        {searchTerm ? (
          `Found ${currentPlaybooks.length || 0} results for "${searchTerm}"`
        ) : (
          `Showing ${currentPlaybooks.length} of ${currentTotal} playbooks`
        )}
      </div>
      <div className="grid gap-4 sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3">
        {currentPlaybooks.map((pkg) => (
          <Card key={pkg.playbookId} className="h-full flex flex-col hover:shadow-md transition-shadow border-gray-200 dark:border-gray-800">
            <CardContent className="p-5 flex flex-col flex-grow">
              <div className="flex-grow">
                <div className="flex items-start justify-between">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                    {pkg.playbookName}
                  </h2>
                  {pkg.playbookProductName && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 ml-2">
                      {pkg.playbookProductName}
                    </span>
                  )}
                </div>

                <p className="mt-2 text-gray-600 dark:text-gray-300 text-sm">
                  {pkg.playbookDescription || 'No description provided.'}
                </p>

                {pkg.playbookTags && pkg.playbookTags.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {pkg.playbookTags.slice(0, 3).map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center rounded-md bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 text-xs font-medium text-blue-700 dark:text-blue-300 ring-1 ring-inset ring-blue-700/10 dark:ring-blue-500/20"
                      >
                        {tag}
                      </span>
                    ))}
                    {pkg.playbookTags.length > 3 && (
                      <span className="text-xs text-gray-500 dark:text-gray-400 self-center">
                        +{pkg.playbookTags.length - 3} more
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800 flex justify-end space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleViewPlaybook(pkg.playbookId)}
                  className="text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 border-gray-300 dark:border-gray-600"
                  disabled={isDeletingId === pkg.playbookId}
                >
                  <Eye className="mr-2 h-4 w-4" />
                  View Details
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDeleteClick(pkg.playbookId)}
                  className="text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30 border-red-200 dark:border-red-800 hover:border-red-300 dark:hover:border-red-700"
                  disabled={isDeletingId === pkg.playbookId}
                >
                  {isDeletingId === pkg.playbookId ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="mr-2 h-4 w-4" />
                  )}
                  Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {loading ? (
        <div className="flex flex-col items-center my-8 gap-2">
          <Spinner className="border-blue-500" />
          <p className="text-center text-muted-foreground">Loading Playbooks...</p>
        </div>
      ) : currentPlaybooks.length === 0 && (
        <Card className="p-8">
          <p className="text-center text-muted-foreground">
            {searchTerm ? 'No playbooks found matching your search.' : 'No playbooks found. Create a new playbook to get started.'}
          </p>
        </Card>
      )}

      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDeleteConfirmed}
        title="Delete Playbook"
        description="This will permanently delete the playbook from the system. This action cannot be undone."
        confirmLabel={isDeletingId ? "Deleting..." : "Delete"}
        cancelLabel="Cancel"
        disabled={Boolean(isDeletingId)}
        requireVerification={true}
        verificationWord="DELETE"
      />

      {showScrollTop && (
        <Button
          variant="secondary"
          size="icon"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-8 right-8 rounded-full shadow-lg bg-blue-500 hover:bg-blue-600 text-white"
        >
          <ChevronUp size={24} />
        </Button>
      )}
    </div>
  );
};

export default PlaybooksList;